import { type FormEvent, useEffect, useRef, useState } from "react";
import type * as api from "./api";
import { confirmMatches, deletedText, type Kind, REASON_MAX, reasonProblem, UNDO_MS } from "./deletion";
import { type Saved, saveFailure } from "./save";

/** The top line of a deleted lead or account page. */
export function DeletedBanner({ kind, deleted, timeZone }: { kind: Kind; deleted: api.Deleted | null | undefined; timeZone: string }) {
  if (!deleted) return null;
  return (
    <p className="deleted-banner" role="note" data-testid={`${kind}-deleted-banner`}>
      {deletedText(kind, deleted, timeZone)}
    </p>
  );
}

/** Delete with a reason and Undo for 10 s; then Restore, and Delete for good (rule U-14). */
export function DeleteRecord({
  kind,
  names,
  deleted,
  canDelete,
  canPurge,
  onDelete,
  onRestore,
  onPurge,
}: {
  kind: Kind;
  names: string[];
  deleted: api.Deleted | null | undefined;
  canDelete: boolean;
  canPurge: boolean;
  onDelete: (reason: string) => Promise<void>;
  onRestore: () => Promise<void>;
  onPurge: (confirm: string) => Promise<void>;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [purging, setPurging] = useState(false);
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Saved | null>(null);
  const [undo, setUndo] = useState(false);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(false), UNDO_MS);
    return () => window.clearTimeout(timer);
  }, [undo]);
  if (!canDelete && !deleted) return null;

  const run = async (work: () => Promise<void>, done: string, offerUndo = false) => {
    setSaving(true);
    setStatus(null);
    setUndo(false);
    try {
      await work();
      setStatus({ ok: true, text: done });
      setUndo(offerUndo);
      setAsking(false);
      setPurging(false);
      setReason("");
      setTyped("");
    } catch (e) {
      setStatus(saveFailure(e, "The change"));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => statusRef.current?.focus());
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problem = reasonProblem(reason);
    if (problem) {
      setStatus({ ok: false, field: true, text: problem });
      reasonRef.current?.focus();
    } else void run(() => onDelete(reason.trim()), `The ${kind} is deleted.`, true);
  };
  const fieldError = status && !status.ok && status.field ? status.text : null;
  const restore = () => void run(onRestore, `The ${kind} is back in the lists.`);

  return (
    <section aria-labelledby={`${kind}-delete`} data-testid={`${kind}-delete`} className="delete-record">
      <h2 id={`${kind}-delete`}>{deleted ? "Deleted" : "Delete"}</h2>
      <p ref={statusRef} tabIndex={-1} role="status" data-testid={`${kind}-delete-status`} className={status && !status.ok ? "error" : undefined}>
        {!fieldError && status?.text}
        {status?.ok && undo && deleted && (
          <>
            {" "}
            <button type="button" data-testid={`${kind}-delete-undo`} disabled={saving} onClick={restore}>
              Undo
            </button>
          </>
        )}
      </p>
      {!deleted && canDelete && !asking && (
        <button type="button" data-testid={`${kind}-delete-open`} onClick={() => setAsking(true)}>
          Delete this {kind}…
        </button>
      )}
      {!deleted && asking && (
        <form className="lead-form" data-testid={`${kind}-delete-form`} onSubmit={submit} noValidate>
          <p>The {kind} leaves the lists. You can restore it for 30 days.</p>
          <label htmlFor={`${kind}-delete-reason`}>Reason</label>
          <input
            ref={reasonRef}
            id={`${kind}-delete-reason`}
            data-testid={`${kind}-delete-reason`}
            value={reason}
            maxLength={REASON_MAX}
            autoFocus
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? `${kind}-delete-reason-error` : undefined}
            onChange={(event) => {
              setReason(event.target.value);
              if (fieldError) setStatus(null);
            }}
          />
          {fieldError && (
            <p id={`${kind}-delete-reason-error`} role="alert" className="error" data-testid={`${kind}-delete-reason-error`}>
              {fieldError}
            </p>
          )}
          <div className="actions">
            <button className="danger" data-testid={`${kind}-delete-confirm`} disabled={saving}>
              Delete
            </button>
            <button type="button" onClick={() => setAsking(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {deleted && canDelete && (
        <div className="actions">
          <button type="button" className="primary" data-testid={`${kind}-restore`} disabled={saving} onClick={restore}>
            Restore
          </button>
          {canPurge && !purging && (
            <button type="button" data-testid={`${kind}-purge-open`} onClick={() => setPurging(true)}>
              Delete for good…
            </button>
          )}
        </div>
      )}
      {deleted && canPurge && purging && (
        <form
          className="lead-form"
          data-testid={`${kind}-purge-form`}
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => onPurge(typed), `The ${kind} is gone for good.`);
          }}
        >
          <p>This cannot be undone. A {kind} with history stays deleted.</p>
          <label htmlFor={`${kind}-purge-name`}>Type “{names[0]}” to delete it for good</label>
          <input id={`${kind}-purge-name`} data-testid={`${kind}-purge-name`} value={typed} autoComplete="off" onChange={(event) => setTyped(event.target.value)} />
          <div className="actions">
            <button className="danger" data-testid={`${kind}-purge-confirm`} disabled={saving || !confirmMatches(typed, ...names)}>
              Delete for good
            </button>
            <button type="button" onClick={() => setPurging(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
